import {
  Cell,
  Pie,
  PieChart as PieChartPrimitive,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import { useMemo } from "react";
import type { Datapoint } from "client/openapi";

const COLORS = [
  "#945bc1",
  "#d86560",
  "#52b09e",
  "#953f3b",
  "#e6a25b",
  "#5293b0",
  "#000000",
];
const RADIAN = Math.PI / 180;

const renderLabel = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  percent,
}: any) => {
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return (
    <text
      x={x}
      y={y}
      fill="#fff"
      textAnchor={x > cx ? "start" : "end"}
      dominantBaseline="central"
    >
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

const Legend = ({
  data,
  excluded,
  toggle,
  color,
}: {
  data: Datapoint[];
  excluded: Set<string>;
  toggle: (name: string) => void;
  color: (name: string) => string;
}) => (
  <div
    style={{
      width: "100%",
      height: "70%",
      overflowY: "auto",
      paddingRight: 4,
      marginTop: "40px",
    }}
  >
    <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
      {data.map(({ name }) => {
        const off = excluded.has(name);
        return (
          <li
            key={name}
            onClick={() => toggle(name)}
            style={{
              cursor: "pointer",
              opacity: off ? 0.5 : 1,
              padding: "4px 8px",
              display: "flex",
              alignItems: "center",
            }}
          >
            <span
              style={{
                width: 16,
                height: 16,
                marginRight: 8,
                borderRadius: 3,
                border: `2px solid ${color(name)}`,
                background: off ? "transparent" : color(name),
              }}
            />
            {name}
          </li>
        );
      })}
    </ul>
  </div>
);

export default function PieChart({
  data = [],
  excluded,
  onToggle,
}: {
  data?: Datapoint[];
  excluded: Set<string>;
  onToggle: (name: string) => void;
}) {
  const allData = data;

  // stable colour assignment (index in original list)
  const color = useMemo(() => {
    const map = new Map<string, string>();
    allData.forEach((d, i) => map.set(d.name, COLORS[i % COLORS.length]));
    return (name: string) => map.get(name) ?? COLORS[0];
  }, [allData]);

  // data actually rendered in the pie
  const visibleData = useMemo(
    () => allData.filter((d) => !excluded.has(d.name)),
    [allData, excluded]
  );

  const hasSubjects = allData.length > 0;
  const hasSelection = visibleData.length > 0;

  return (
    <div style={{ display: "flex", width: "100%", height: "100%" }}>
      <div style={{ flex: "0 0 60%", height: "100%" }}>
        {!hasSubjects ? (
          <div className="h-full flex items-center justify-center text-sm text-gray-500">
            No subjects recorded for this range.
          </div>
        ) : !hasSelection ? (
          <div className="h-full flex items-center justify-center text-sm text-gray-500">
            Select at least one subject to view the chart.
          </div>
        ) : (
          <ResponsiveContainer>
            <PieChartPrimitive>
              <Tooltip />
              <Pie
                data={visibleData}
                dataKey="count"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={100}
                labelLine={false}
                label={renderLabel}
                onClick={({ name }) => name && onToggle(name)}
                animationDuration={300}
              >
                {visibleData.map((d) => (
                  <Cell key={d.name} fill={color(d.name)} />
                ))}
              </Pie>
            </PieChartPrimitive>
          </ResponsiveContainer>
        )}
      </div>
      <div
        style={{
          flex: "0 0 40%",
          display: "flex",
          alignItems: "stretch",
          justifyContent: "flex-start",
          paddingLeft: 20,
        }}
      >
        <Legend
          data={allData}
          excluded={excluded}
          toggle={onToggle}
          color={color}
        />
      </div>
    </div>
  );
}
